import {
  AccionesRol,
  CategoriaDispositivo,
  EstadoLiquidacion,
  FrecuenciaAjustePrecio,
  ModoPrecio,
  PeriodicidadPrecio,
  TipoAjustePrecio,
  TipoCargo,
} from 'modelos/src';

/** Ruta del módulo durante la convivencia con la Facturación anterior. */
export const RUTA_FACTURACION = '/main/facturacion-v2';

export interface SolapaFacturacion {
  ruta: string;
  label: string;
  icono: string;
  permisos: AccionesRol[];
}

export const SOLAPAS_FACTURACION: SolapaFacturacion[] = [
  {
    ruta: 'dashboard',
    label: 'Dashboard',
    icono: 'dashboard',
    permisos: ['Facturación - Ver panel'],
  },
  {
    ruta: 'productos',
    label: 'Productos',
    icono: 'shopping_bag',
    permisos: ['Facturación - Ver productos'],
  },
  {
    ruta: 'precios',
    label: 'Precios',
    icono: 'sell',
    permisos: ['Facturación - Ver precios'],
  },
  {
    ruta: 'contrataciones',
    label: 'Contrataciones',
    icono: 'handshake',
    permisos: ['Facturación - Ver contrataciones'],
  },
  {
    ruta: 'liquidaciones',
    label: 'Liquidaciones',
    icono: 'receipt_long',
    permisos: ['Facturación - Ver liquidaciones'],
  },
  {
    ruta: 'configuracion',
    label: 'Configuración',
    icono: 'settings',
    permisos: ['Facturación - Editar configuración de facturación'],
  },
];

/** Cualquier permiso de lectura del módulo habilita la entrada del menú. */
export const PERMISOS_VER_FACTURACION: AccionesRol[] =
  SOLAPAS_FACTURACION.flatMap((s) => s.permisos);

/** Color de cada categoría de dispositivo (RGB), para sus chips. */
export const COLOR_CATEGORIA: Record<CategoriaDispositivo, string> = {
  alarmas: '229, 57, 53',
  trackers: '30, 136, 229',
  sirenas: '251, 140, 0',
  camaras: '142, 36, 170',
  luminarias: '230, 160, 0',
  dispositivosLorawan: '0, 137, 123',
  gateways: '57, 73, 171',
  botonesBluetooth: '0, 151, 167',
};

/** Ícono de cada categoría de dispositivo, para chips y listados. */
export const ICONO_CATEGORIA: Record<CategoriaDispositivo, string> = {
  alarmas: 'doorbell',
  trackers: 'gps_fixed',
  sirenas: 'campaign',
  camaras: 'videocam',
  luminarias: 'lightbulb',
  dispositivosLorawan: 'router',
  gateways: 'podcasts',
  botonesBluetooth: 'touch_app',
};

export const ETIQUETA_CATEGORIA: Record<CategoriaDispositivo, string> = {
  alarmas: 'Alarmas',
  trackers: 'Trackers',
  sirenas: 'Sirenas',
  camaras: 'Cámaras',
  luminarias: 'Luminarias',
  dispositivosLorawan: 'Dispositivos LoRaWAN',
  gateways: 'Gateways',
  botonesBluetooth: 'Botones BLE',
};

export const CATEGORIAS = Object.keys(
  ETIQUETA_CATEGORIA,
) as CategoriaDispositivo[];

export const ETIQUETA_TIPO_CARGO: Record<TipoCargo, string> = {
  Recurrente: 'Recurrente',
  UnicaVez: 'Única vez',
  PorUso: 'Por uso',
};

export const ETIQUETA_MODO: Record<ModoPrecio, string> = {
  PorUnidad: 'Por unidad',
  PorCliente: 'Por cliente',
  Escalonado: 'Escalonado',
  Volumen: 'Por volumen',
};

export const AYUDA_MODO: Record<ModoPrecio, string> = {
  PorUnidad: 'Cantidad × precio. Una contratación por dispositivo.',
  PorCliente:
    'Precio fijo, sin importar la cantidad. Una sola contratación por cliente.',
  Escalonado:
    'Cada tramo se cobra a su precio y se suman (como las escalas de un impuesto).',
  Volumen:
    'Toda la cantidad se cobra al precio del tramo en el que cae el total.',
};

export const PERIODICIDADES: PeriodicidadPrecio[] = [
  'Mensual',
  'Bimestral',
  'Trimestral',
  'Semestral',
  'Anual',
];

export const ETIQUETA_AJUSTE: Record<TipoAjustePrecio, string> = {
  PorcentajeFijo: 'Porcentaje fijo',
  IPC: 'Según el IPC',
};

/** Meses entre un ajuste automático y el siguiente. */
export const MESES_FRECUENCIA_AJUSTE: Record<FrecuenciaAjustePrecio, number> = {
  Trimestral: 3,
  Semestral: 6,
  Anual: 12,
};

export const FRECUENCIAS_AJUSTE = Object.keys(
  MESES_FRECUENCIA_AJUSTE,
) as FrecuenciaAjustePrecio[];

export const ALICUOTAS = [21, 10.5, 0];

export const CLASE_ESTADO_LIQUIDACION: Record<EstadoLiquidacion, string> = {
  Borrador: 'fact-chip fact-chip-gris',
  Emitida: 'fact-chip fact-chip-azul',
  Cobrada: 'fact-chip fact-chip-verde',
  Anulada: 'fact-chip fact-chip-rojo',
};

/** Cómo cubre una contratación: un dispositivo, un grupo, todos o el cliente. */
export type FormaContratacionVista =
  | 'Individual'
  | 'Grupo'
  | 'Todos'
  | 'Cliente';

export const ETIQUETA_FORMA: Record<FormaContratacionVista, string> = {
  Individual: 'Un dispositivo',
  Grupo: 'Un grupo',
  Todos: 'Todos los dispositivos',
  Cliente: 'Por cliente',
};

export const ICONO_FORMA: Record<FormaContratacionVista, string> = {
  Individual: 'router',
  Grupo: 'workspaces',
  Todos: 'select_all',
  Cliente: 'business',
};

/** Categorías que admiten "todos los dispositivos". */
export const CATEGORIAS_TODOS: CategoriaDispositivo[] = [
  'alarmas',
  'trackers',
  'luminarias',
];

/** Las contrataciones anteriores al campo `forma` se infieren del dispositivo. */
export function formaDe(c: {
  forma?: string;
  idEntidad?: string;
}): FormaContratacionVista {
  return (
    (c.forma as FormaContratacionVista) ??
    (c.idEntidad ? 'Individual' : 'Cliente')
  );
}

export function esConjunto(c: { forma?: string; idEntidad?: string }): boolean {
  const f = formaDe(c);
  return f === 'Grupo' || f === 'Todos';
}
